/**
 * Firebase Cloud Messaging (FCM) 유틸리티
 * - FCM 토큰 관리
 * - 메시지 수신 및 처리
 * - 알림 관련 기능
 */

import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { getDatabase, ref, set, onValue, get, remove } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { showToast } from './ui-manager.js';

// Firebase 관련 모듈 임포트
import { firebaseApp, auth, database } from './firebase-config';

// FCM 메시징 초기화
let messaging = null;
try {
  // 브라우저가 FCM을 지원하는지 확인
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    (async () => {
      if (await isSupported()) {
        messaging = getMessaging(firebaseApp);
        console.log('Firebase Cloud Messaging이 초기화되었습니다.');
      } else {
        console.warn('이 브라우저는 Firebase Cloud Messaging을 지원하지 않습니다.');
      }
    })();
  }
} catch (error) {
  console.error('Firebase Cloud Messaging 초기화 오류:', error);
}

/**
 * 알림 권한 요청 및 FCM 토큰 얻기
 * @returns {Promise<string|null>} FCM 토큰 또는 null
 */
export const requestNotificationPermission = async () => {
  if (!messaging) {
    console.warn('Firebase 메시징이 지원되지 않습니다.');
    return null;
  }

  try {
    // 푸시 알림 권한 요청
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('알림 권한이 거부되었습니다.');
      return null;
    }

    console.log('알림 권한이 허용되었습니다.');

    // 서비스 워커 등록
    const serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    
    // FCM 토큰 가져오기
    const token = await getToken(messaging, {
      // vapidKey: 'BKy-GomQ8ZOX3lZBzJg0KALjVF207dm8Hme5FBBumha1sOspfl7rPJ_Akisaq9Bi9xDnInk-Ll6-DJ2dxmil0II',
      // vapidKey: 'BHLKPyrM7UEDK9zRlDdSA8e-7rDVbrmX0C7JmgpntTKrLq5lLm8tJOCuBEGJaZnN7kFU6OsC5u1LsICcwZoZrvc',
      vapidKey: 'BBDyHvBi6XWfgcOKxtNiRx9ZgD8Ocy6gzwk-C-XOzsRaa53kMQaJWFRQPPU3W-u--tzT0njMW4wKAM4jX1XCMmE',
      serviceWorkerRegistration: serviceWorkerRegistration
    });

    if (token) {
      console.log('FCM 토큰을 획득했습니다:', token);
      
      // 현재 사용자가 로그인되어 있으면 토큰 저장
      const currentUser = auth.currentUser;
      if (currentUser) {
        await saveFCMToken(currentUser.uid, token);
      }
      
      return token;
    } else {
      console.error('FCM 토큰을 획득할 수 없습니다.');
      return null;
    }
  } catch (error) {
    console.error('알림 권한 요청 오류:', error);
    return null;
  }
};

/**
 * FCM 토큰을 Firebase Database에 저장
 * @param {string} userId - 사용자 ID
 * @param {string} token - FCM 토큰
 * @returns {Promise<boolean>} 저장 성공 여부
 */
export const saveFCMToken = async (userId, token) => {
  if (!userId || !token) {
    console.error('사용자 ID와 토큰이 필요합니다.');
    return false;
  }

  try {
    // 브라우저 정보 가져오기
    const browserInfo = _getBrowserInfo();
    
    // 현재 시간 및 기기 정보와 함께 토큰 저장
    const tokenData = {
      token: token,
      createdAt: new Date().toISOString(),
      lastUsed: new Date().toISOString(),
      device: {
        userAgent: navigator.userAgent,
        browser: browserInfo.browser,
        browserVersion: browserInfo.version,
        platform: navigator.platform,
        language: navigator.language
      }
    };
    
    // 각 사용자마다 여러 기기의 토큰을 고유 ID로 저장
    const tokenId = `${browserInfo.browser}_${Date.now()}`;
    const tokenRef = ref(database, `users/${userId}/fcmTokens/${tokenId}`);
    
    await set(tokenRef, tokenData);
    console.log('FCM 토큰이 저장되었습니다.');
    return true;
  } catch (error) {
    console.error('FCM 토큰 저장 오류:', error);
    return false;
  }
};

/**
 * 사용자의 FCM 토큰 목록 가져오기
 * @param {string} userId - 사용자 ID
 * @returns {Promise<string[]>} FCM 토큰 배열
 */
export const getUserFCMTokens = async (userId) => {
  if (!userId) {
    console.error('사용자 ID가 필요합니다.');
    return [];
  }

  try {
    const tokensRef = ref(database, `users/${userId}/fcmTokens`);
    const snapshot = await get(tokensRef);
    
    if (!snapshot.exists()) {
      return [];
    }
    
    const tokensData = snapshot.val();
    const tokens = [];
    
    // 유효한 토큰만 필터링
    for (const [tokenId, tokenData] of Object.entries(tokensData)) {
      if (tokenData && tokenData.token) {
        tokens.push(tokenData.token);
      }
    }
    
    return tokens;
  } catch (error) {
    console.error('FCM 토큰 조회 오류:', error);
    return [];
  }
};

/**
 * 알림 메시지 수신 리스너 설정
 * @param {Function} callback - 메시지가 수신될 때 호출될 콜백 함수
 */
export const setupNotificationListener = (callback) => {
  if (!messaging) {
    console.warn('Firebase 메시징이 지원되지 않습니다.');
    return;
  }

  // 메시지 수신 리스너
  onMessage(messaging, (payload) => {
    console.log('메시지가 수신되었습니다:', payload);
    
    // 브라우저 알림 생성
    const notificationTitle = payload.notification?.title || '새 메시지';
    const notificationOptions = {
      body: payload.notification?.body || '',
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      data: payload.data,
      requireInteraction: true
    };
    
    // 브라우저가 알림을 지원하는지 확인
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(notificationTitle, notificationOptions);
      
      // 알림 클릭 처리
      notification.onclick = function() {
        notification.close();
        
        // 채팅방 ID가 있으면 해당 채팅방으로 이동
        if (payload.data?.chatRoomId) {
          window.location.href = `/chat?roomId=${payload.data.chatRoomId}`;
        } else {
          // 활성화된 창이 없으면 애플리케이션을 연다
          if (window.parent === window) {
            window.focus();
          }
        }
        
        // 콜백 호출
        if (typeof callback === 'function') {
          callback(payload);
        }
      };
    }
  });
};

/**
 * 브라우저 정보 가져오기
 * @private
 * @returns {{browser: string, version: string}} 브라우저 이름과 버전
 */
const _getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  let browser = 'unknown';
  let version = 'unknown';
  
  // Chrome
  if (/Chrome/.test(userAgent) && !/Edge/.test(userAgent) && !/Edg/.test(userAgent)) {
    browser = 'Chrome';
    version = userAgent.match(/Chrome\/(\d+\.\d+)/)?.[1] || '';
  }
  // Firefox
  else if (/Firefox/.test(userAgent)) {
    browser = 'Firefox';
    version = userAgent.match(/Firefox\/(\d+\.\d+)/)?.[1] || '';
  }
  // Safari
  else if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
    browser = 'Safari';
    version = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || '';
  }
  // Edge
  else if (/Edge/.test(userAgent) || /Edg/.test(userAgent)) {
    browser = 'Edge';
    version = userAgent.match(/Edge\/(\d+\.\d+)/)?.[1] || userAgent.match(/Edg\/(\d+\.\d+)/)?.[1] || '';
  }
  // IE
  else if (/MSIE/.test(userAgent) || /Trident/.test(userAgent)) {
    browser = 'IE';
    version = userAgent.match(/MSIE (\d+\.\d+)/)?.[1] || '';
    if (!version) {
      version = userAgent.match(/rv:(\d+\.\d+)/)?.[1] || '';
    }
  }
  
  return { browser, version };
};

export { messaging, auth, database }; 